// ── 직원 관리 — 한 목록(4대보험·3.3%) (2026-11-03 미남님 "직원·마케터 합치기", 예전 [직원|마케터] 탭) ──────
//   이 회사 직원 목록 + 이 회사 담당 3.3% 목록(담당 연결이 정본, 2026-09-08)을 한 사람 한 줄로 합쳐 [전체·4대보험·3.3%] 로 거른다.
import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { useMarketerPermissions } from './useMarketerPermissions';
import { useUserEdit } from './useUserEdit';
import { useBulkRegister } from './useBulkRegister';
import { PeopleTableView } from './PeopleTableView';
import { UserModals } from './UserModals';
import { useDepartmentSettings } from './useDepartmentSettings';
import { useAttendanceTypeOptions } from './useAttendanceTypeOptions';
import { useMarketerLinks } from './useMarketerLinks';
import { useUserList } from './useUserList';
import { usePeopleView } from './usePeopleView';
import MarketerAssignModal from './MarketerAssignModal';
import { KIND_ALL, KIND_INSURED, KIND_FREELANCE, KIND_LABEL } from './userRowKind';
import StaffLimitBadge from './StaffLimitBadge';
import StaffToolsMenu from './StaffToolsMenu';
import { useHqCompany } from '../initialsetup/useHqCompany';
import { STD_HEADER_TOP } from '../adminlayout/designStandard';
import {
  pageTitleXLStyle,
  pageSubtitleStyle,
  topRowStyle,
  topActionsStyle,
  tabGroupStyle,
  tabBtn,
  blackBtn,
  outlineBtn,
  searchWrapStyle,
  searchInputStyle,
  searchIconStyle,
} from './styles';

export default function UserManagement({
  pageTitle = '직원 관리',
  apiBase = '/api/admin/users',
  entityLabel = '직원',
  showBulk = false,
  companyCode = 'PINE',
  departmentType,
}) {
  const { showToast } = useToast();
  const normalizedCompanyCode = String(companyCode || 'PINE').trim().toUpperCase() || 'PINE';
  const { isHq } = useHqCompany(normalizedCompanyCode); // 본사 부서 = internal (장부의 사이트 본사, 예전 'PINE' 글자 비교 — 미팅링크 LINK 가 external 로 저장됐다)
  const resolvedDepartmentType = departmentType || (isHq ? 'internal' : 'external');
  const { canManageMarketers, canManageCompanyMarketers, canAssignMarketers, canSeeMarketerTab } =
    useMarketerPermissions({ apiBase, companyCode: normalizedCompanyCode });

  const [assignOpen, setAssignOpen] = useState(false); // 마케터 지정 창(☰ 메뉴에서 연다)

  const { attendanceTypeOptions, createAttendanceType, getDefaultAttendanceType } = useAttendanceTypeOptions({
    apiBase, companyCode: normalizedCompanyCode, showToast,
  });

  // 이 회사 직원 목록·재직/전체·휴대폰 너비·IP 별칭 (useUserList.js)
  const {
    isMobile, includeResigned, setIncludeResigned, list, loading, ipAliases, fetchUsers,
  } = useUserList({ apiBase, entityLabel, attendanceTypeOptions, showToast });

  const departmentSettings = useDepartmentSettings({
    companyCode: normalizedCompanyCode, deptType: resolvedDepartmentType,
    checkAccess: true, // ☰ 부서 설정·등록 창 '+ 새 부서 추가'를 보일지(서버 200 일 때만)
    showToast,
    onChanged: fetchUsers,
  });
  const departments = departmentSettings.departmentNames;
  // 3.3% 부서 목록 — 4대보험과 따로, 이 회사 테넌트에 하나(2026-11-03 합치기-2). 바뀌면 두 목록 다 다시 읽는다(이름 바꾸면 사람 부서도 바뀜).
  const freelancerDepartmentSettings = useDepartmentSettings({
    companyCode: normalizedCompanyCode, kind: 'freelancer',
    checkAccess: true,
    showToast,
    onChanged: () => refreshAll(),
  });

  const marketerHook = useMarketerLinks({
    companyCode: normalizedCompanyCode,
    includeResigned,
    showToast,
    enabled: canSeeMarketerTab, // 3.3% 줄을 볼 수 있는 사람만 불러온다(예전 마케터 탭과 같은 판정, 못 받으면 빈 목록)
  });
  const view = usePeopleView({ list, marketers: marketerHook.marketers, includeResigned });
  // 고친 뒤에는 두 목록을 함께 다시 읽는다(3.3% 줄은 담당 목록에서도 온다)
  const refreshAll = () => { fetchUsers(); if (canSeeMarketerTab) marketerHook.fetchMarketers(); };
  const linkedIds = useMemo(() => new Set(marketerHook.marketers.map((m) => String(m.id))), [marketerHook.marketers]);

  const editHook = useUserEdit({
    showToast,
    attendanceTypeOptions,
    departments,
    ipAliases,
    apiBase,
    entityLabel,
    fetchUsers: refreshAll,
    // 3.3% 로 새로 등록한 사람은 곧바로 이 회사 담당으로 연결한다.
    onMarketerCreated: (newId) => marketerHook.linkMarketer(newId),
    // 등록 창에서 3.3% 를 고를 수 있는가 — 예전 [+ 마케터 등록] 버튼과 같은 판정
    canCreateFreelancer: canManageMarketers || canManageCompanyMarketers,
    // 등록 권한을 회사 단위로 판정하기 위해 서버로 함께 보낸다 (2026-09-09).
    marketerCompanyCode: normalizedCompanyCode,
  });

  // 일괄등록(showBulk 화면만) = 4대보험 직원. 3.3% 일괄등록은 테넌트 관리 → 초기설정 [4대보험|3.3%].
  const bulkHook = useBulkRegister({
    showToast,
    departments,
    getDefaultAttendanceType,
    fetchUsers,
    apiBase,
    fixedDepartment: '',
  });

  // 묶음 칸 — 글자가 두 줄로 꺾이지 않게, 좁은 휴대폰(360)에도 네 칸이 들어가게 좌우 여백만 줄인다
  const filterChip = (active) => ({ ...tabBtn(active), whiteSpace: 'nowrap', ...(isMobile ? { padding: '5px 10px' } : {}) });

  return (
    <div>
      {/* 2026-08-26 디자인 실험: 카드 박스 제거 — 홈처럼 텍스트 헤더 + 상황판 버튼 2종(검정/흰 외곽선) */}
      {/* 제목 시작 높이 = 사이드바 '홈' 메뉴와 나란히 (2026-08-26 사용자 명세: 상단 여백 확대) */}
      <div style={{ ...topRowStyle, position: 'relative', alignItems: isMobile ? 'stretch' : 'flex-end', margin: isMobile ? '4px 0 16px' : `${STD_HEADER_TOP}px 0 16px` }}>
        <div>
          <h2 style={pageTitleXLStyle}>{pageTitle}</h2>
          <div style={pageSubtitleStyle}>
            직원(4대보험·3.3%)을 등록·수정·퇴직 처리합니다.
            <StaffLimitBadge apiBase={apiBase} listKey={list} marketerKey={marketerHook.marketers} />{/* 인원 n / 한도(요금제 4-3) */}
          </div>
        </div>

        <div style={topActionsStyle(isMobile)}>
          {/* 2026-10-26: 3.3% 일괄등록은 테넌트 관리 → 회사 → 초기설정 '직원 일괄등록'(창 위 [4대보험|3.3%]) — 여기선 showBulk 화면만. */}
          {showBulk && (
            <button style={outlineBtn} onClick={() => bulkHook.setBulkOpen(true)}>
              일괄등록
            </button>
          )}

          {/* 등록은 하나 — 창 맨 위에서 구분(4대보험/3.3%)을 매번 고른다(결정 3b). 3.3% 는 운영자·이 회사 직원관리만 고를 수 있다. */}
          {/* 휴대폰: 일괄등록이 없으면 등록 버튼 혼자라 한 줄 전체로 */}
          <button style={{ ...blackBtn, ...(isMobile && !showBulk ? { gridColumn: '1 / -1' } : {}) }} onClick={() => editHook.openCreate()}>
            + {entityLabel} 등록
          </button>
          {/* ☰ 부서 설정(IP 관리처럼, 2026-10-26)·마케터 지정(2026-11-04 버튼에서 옮김) — 휴대폰은 ☰ 가 혼자 한 줄을 차지하지 않게 제목 줄 오른쪽 끝으로 */}
          <StaffToolsMenu
            settings={departmentSettings}
            freelancerSettings={freelancerDepartmentSettings}
            // 다른 회사에 이미 있는 3.3% 인원을 이 회사 담당으로 — 지정할 수 있는 사람에게만 항목이 생긴다
            onAssignMarketer={canAssignMarketers ? () => { marketerHook.fetchCandidates(); setAssignOpen(true); } : null}
            style={isMobile ? { position: 'absolute', top: 0, right: 0 } : undefined}
          />
        </div>
      </div>

      {/* 검색(성명·부서·아이디) + 한 묶음(2026-11-04 맵찔찔이님 a): 구분 [전체·4대보험·3.3%] 중 하나 | [퇴직 포함] 켜고 끄기.
          처음은 재직 중인 사람만, 켜면 세 칸 숫자도 퇴직자까지 센다. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 40 }}>
        <div style={{ ...searchWrapStyle(isMobile), marginBottom: 0, flex: isMobile ? '1 1 100%' : '0 1 400px' }}>
          <Search size={15} style={searchIconStyle} />
          <input
            style={searchInputStyle}
            placeholder={`${entityLabel} 검색 (성명·부서·아이디)`}
            value={view.searchQuery}
            onChange={(e) => view.setSearchQuery(e.target.value)}
          />
        </div>
        <div style={{ ...tabGroupStyle, alignItems: 'center' }} role="group" aria-label="구분 거르기">
          {[KIND_ALL, KIND_INSURED, KIND_FREELANCE].map((k) => (
            <button key={k} style={filterChip(view.kind === k)} onClick={() => view.setKind(k)}>
              {k === KIND_ALL ? '전체' : KIND_LABEL[k]} ({view.counts[k]})
            </button>
          ))}
          <span aria-hidden="true" style={{ width: 1, height: 16, margin: '0 4px', background: 'var(--border)', flexShrink: 0 }} />
          <button
            aria-pressed={includeResigned}
            style={filterChip(includeResigned)}
            onClick={() => setIncludeResigned((on) => !on)}
          >
            퇴직 포함
          </button>
        </div>
      </div>

      <PeopleTableView
        pagedList={view.pagedList}
        list={view.filteredList}
        loading={loading || marketerHook.loading}
        isMobile={isMobile}
        attendanceTypeOptions={attendanceTypeOptions}
        companyCode={normalizedCompanyCode}
        includeResigned={includeResigned}
        perms={{ canManageMarketers, canManageCompanyMarketers, canAssignMarketers }}
        currentPage={view.currentPage}
        totalPages={view.totalPages}
        onEdit={editHook.openEdit}
        onResetPassword={editHook.resetPassword}
        onDeactivate={editHook.deactivate}
        onUnlink={marketerHook.unlinkMarketer}
        onPageChange={view.setCurrentPage}
      />

      <MarketerAssignModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        companyCode={normalizedCompanyCode}
        candidates={marketerHook.candidates}
        linkedIds={linkedIds}
        linkMarketer={marketerHook.linkMarketer}
      />

      <UserModals
        editHook={editHook}
        bulkHook={bulkHook}
        showBulk={showBulk}
        entityLabel={entityLabel}
        departmentSettings={departmentSettings}
        freelancerDepartmentSettings={freelancerDepartmentSettings}
        onCreateAttendanceType={createAttendanceType}
        ipAliases={ipAliases}
      />

    </div>
  );
}
